// Locator generation: shortest unique CSS selector, readable DOM path, XPath.
// Bottom rung of the attribution ladder — always available on any stack.

const cssEscape = (s: string): string =>
  typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(s) : s.replace(/([^\w-])/g, '\\$1')

function classSegment(el: Element): string | null {
  const classes = Array.from(el.classList).slice(0, 3)
  if (!classes.length) return null
  return el.tagName.toLowerCase() + classes.map((c) => `.${cssEscape(c)}`).join('')
}

function nthSegment(el: Element): string {
  const tag = el.tagName.toLowerCase()
  const parent = el.parentElement
  if (!parent) return tag
  const same = Array.from(parent.children).filter((c) => c.tagName === el.tagName)
  if (same.length === 1) return tag
  return `${tag}:nth-of-type(${same.indexOf(el) + 1})`
}

function isUnique(selector: string, el: Element): boolean {
  try {
    const found = el.ownerDocument.querySelectorAll(selector)
    return found.length === 1 && found[0] === el
  } catch {
    return false
  }
}

/** Shortest selector that uniquely identifies the element in its document. */
export function cssSelector(el: Element): string {
  if (el.id) {
    const byId = `#${cssEscape(el.id)}`
    if (isUnique(byId, el)) return byId
  }
  const cls = classSegment(el)
  if (cls && isUnique(cls, el)) return cls

  // Build ancestor path until unique, preferring readable segments.
  const segments: string[] = []
  let node: Element | null = el
  while (node && node.tagName.toLowerCase() !== 'html') {
    if (node.id) {
      segments.unshift(`#${cssEscape(node.id)}`)
      const sel = segments.join(' > ')
      if (isUnique(sel, el)) return sel
      segments.shift()
    }
    segments.unshift(classSegment(node) ?? nthSegment(node))
    const sel = segments.join(' > ')
    if (isUnique(sel, el)) return sel
    // Class segment not unique at this depth — fall back to positional for this level.
    segments[0] = nthSegment(node)
    const positional = segments.join(' > ')
    if (isUnique(positional, el)) return positional
    node = node.parentElement
  }
  return ['html', ...segments].join(' > ')
}

/** Human-readable full path: html > body > main > button.checkout-cta */
export function domPath(el: Element): string {
  const parts: string[] = []
  let node: Element | null = el
  while (node) {
    let part = node.tagName.toLowerCase()
    if (node.id) part += `#${node.id}`
    else if (node.classList.length) part += `.${Array.from(node.classList).slice(0, 2).join('.')}`
    parts.unshift(part)
    node = node.parentElement
  }
  return parts.join(' > ')
}

/** Positional XPath fallback locator. */
export function xPath(el: Element): string {
  const parts: string[] = []
  let node: Element | null = el
  while (node && node.nodeType === 1) {
    const parent: Element | null = node.parentElement
    const same = parent ? Array.from(parent.children).filter((c) => c.tagName === node!.tagName) : [node]
    const idx = same.length > 1 ? `[${same.indexOf(node) + 1}]` : ''
    parts.unshift(node.tagName.toLowerCase() + idx)
    node = parent
  }
  return '/' + parts.join('/')
}
